import debounce from 'lodash/debounce';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {i18n} from 'src/i18n';
import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import ModeloTareaService from 'src/modules/modeloTarea/modeloTareaService';
import Errors from 'src/modules/shared/error/errors';
import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import {
  filterParentFieldLayoutConfigByKeys,
  normalizeParentFieldLayoutConfig,
  remapPlastyvergParentFieldLabel,
  DEFAULT_PARENT_FIELD_CELLS_PER_ROW,
  DEFAULT_PARENT_FIELD_COL_SPAN,
  ParentFieldLayoutConfig,
  reorderParentFieldLayout,
  setParentFieldColSpanInLayout,
  setParentFieldLayoutCellsPerRow,
  toggleParentFieldInLayout,
} from 'src/modules/tarea/parentFieldLayout';
import {
  isPlastyvergParentFieldsMapKey,
  mergeParentTaskForConfig,
  plastyvergMapKeyFromModeloTitulo,
  putParentFieldsConfigModeloCache,
  resolveParentFieldsConfigModelo,
  resolveParentFieldsMapKey,
  resolvePlastyvergCamposSeleccionadosPrefix,
  resolveStoredParentFieldLayout,
  sanitizePlastyvergParentFieldsMap,
} from 'src/modules/tarea/plastyvergParentFieldsConfig';
import {formatProcedureAnswerText, getRecordId} from 'src/modules/tarea/tareaParcialHelpers';

const getQuestionRawLabel = (item: any, fallback: string) =>
  item?.pregunta?.label || item?.pregunta?.titulo || item?.label || item?.titulo || fallback;

const getQuestionLabel = (item: any, fallback: string) =>
  remapPlastyvergParentFieldLabel(getQuestionRawLabel(item, fallback));

const getQuestionKey = (item: any, index: number) =>
  item?.pregunta?.id || item?.id || `${getQuestionRawLabel(item, 'pregunta')}-${item?.tipoRespuesta || ''}-${index}`;

export const getProcedureQuestions = (task: any) =>
  (task?.listaProcedimiento || [])
    .map((item, index) => ({item, index}))
    .filter(({item}) => item?.tipo === 'pregunta' || item?.pregunta)
    .map(({item, index}) => ({
      key: getQuestionKey(item, index),
      label: getQuestionLabel(item, i18n('entities.firma.parcial.blankQuestion')),
      item,
      answer: formatProcedureAnswerText(item, '-'),
    }));

const questionsSignature = (questions: Array<{key: string}>) => questions.map((q) => q.key).join('|');

type UseParentFieldsLayoutParams = {
  parentTask: any;
  fallbackParentTask?: any;
  childTasks?: any[];
  listChildren?: any[];
  tenantId?: string | null;
  enabled?: boolean;
  /** true cuando el pipeline de datos ya puede alimentar layout (ready / parent disponible). */
  dataReady?: boolean;
  onConfigModeloAppFieldsUpdated?: (childModelId: string, nextAppFields: any) => void;
  layoutMapKey?: 'ID-23' | 'ID-24' | null;
};

export function useParentFieldsLayout({
  parentTask,
  fallbackParentTask,
  childTasks = [],
  listChildren = [],
  tenantId,
  enabled = true,
  dataReady = true,
  onConfigModeloAppFieldsUpdated,
  layoutMapKey = null,
}: UseParentFieldsLayoutParams) {
  const resolvedTenantId = tenantId || AuthCurrentTenant.get();
  const parentId = getRecordId(parentTask) || getRecordId(fallbackParentTask);
  const configParent = useMemo(
    () => mergeParentTaskForConfig(parentTask, fallbackParentTask),
    [fallbackParentTask, parentTask],
  );
  const questions = useMemo(() => getProcedureQuestions(configParent), [configParent]);
  const qSignature = useMemo(() => questionsSignature(questions), [questions]);

  const sourceChildren = childTasks.length ? childTasks : listChildren;

  const [layout, setLayout] = useState<ParentFieldLayoutConfig>({
    cellsPerRow: DEFAULT_PARENT_FIELD_CELLS_PER_ROW,
    fields: [],
  });
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userEdited, setUserEdited] = useState(false);
  const [hydrationTimedOut, setHydrationTimedOut] = useState(false);

  const readyRef = useRef(false);
  const userEditedRef = useRef(false);
  const configModeloRef = useRef<any | null>(null);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const onAppFieldsUpdatedRef = useRef(onConfigModeloAppFieldsUpdated);
  onAppFieldsUpdatedRef.current = onConfigModeloAppFieldsUpdated;
  const tenantIdRef = useRef(resolvedTenantId);
  tenantIdRef.current = resolvedTenantId;
  const layoutMapKeyRef = useRef(layoutMapKey);
  layoutMapKeyRef.current = layoutMapKey;

  const resetForParent = useCallback(() => {
    readyRef.current = false;
    userEditedRef.current = false;
    configModeloRef.current = null;
    setReady(false);
    setUserEdited(false);
    setHydrationTimedOut(false);
    setLayout({cellsPerRow: DEFAULT_PARENT_FIELD_CELLS_PER_ROW, fields: []});
  }, []);

  useEffect(() => {
    resetForParent();
  }, [parentId, layoutMapKey, resetForParent]);

  const persistTableConfig = useMemo(
    () =>
      debounce(async (childModeloTarea: any, mapKey: string, parentFieldLayout: ParentFieldLayoutConfig) => {
        const childModelId = getRecordId(childModeloTarea);
        if (!childModelId || !mapKey) return;
        const activeTenantId = tenantIdRef.current;
        setSaving(true);
        try {
          const freshModelo = await ModeloTareaService.find(childModelId);
          const previousView = freshModelo?.appFields?.childTasksTableView || {};
          const previousByParent = previousView.parentQuestionKeysByParentModel || {};

          // Plastyverg (o destino ID-23/24): key canónica + purge de tituloTarea / ids legacy.
          const canonicalKey =
            (isPlastyvergParentFieldsMapKey(mapKey) && mapKey) ||
            plastyvergMapKeyFromModeloTitulo(freshModelo?.titulo || childModeloTarea?.titulo) ||
            mapKey;
          const usePlastyvergMap =
            isPlastyvergTenant(activeTenantId) || isPlastyvergParentFieldsMapKey(canonicalKey);

          if (usePlastyvergMap && !isPlastyvergParentFieldsMapKey(canonicalKey)) {
            console.warn(
              '[useParentFieldsLayout] Persist Plastyverg abortado: key no canónica',
              canonicalKey,
            );
            return;
          }

          const nextByParent = usePlastyvergMap
            ? {
                ...sanitizePlastyvergParentFieldsMap(previousByParent),
                [canonicalKey]: parentFieldLayout,
              }
            : {
                ...previousByParent,
                [canonicalKey]: parentFieldLayout,
              };

          const nextAppFields = {
            ...(freshModelo?.appFields || childModeloTarea?.appFields || {}),
            childTasksTableView: {
              ...previousView,
              parentQuestionKeysByParentModel: nextByParent,
            },
          };
          await ModeloTareaService.update(childModelId, {appFields: nextAppFields});
          configModeloRef.current = {
            ...(configModeloRef.current || freshModelo || childModeloTarea),
            id: childModelId,
            titulo: freshModelo?.titulo || childModeloTarea?.titulo,
            appFields: nextAppFields,
          };
          putParentFieldsConfigModeloCache(activeTenantId, configModeloRef.current);
          onAppFieldsUpdatedRef.current?.(childModelId, nextAppFields);
        } catch (error) {
          Errors.handle(error);
        } finally {
          setSaving(false);
        }
      }, 1000),
    [],
  );

  useEffect(() => () => persistTableConfig.cancel(), [persistTableConfig]);

  useEffect(() => {
    if (!enabled || !dataReady) return;
    if (questions.length === 0) {
      readyRef.current = true;
      setReady(true);
      return;
    }
    if (userEditedRef.current) return;

    const plastyvergPrefix = isPlastyvergTenant(resolvedTenantId)
      ? layoutMapKey || resolvePlastyvergCamposSeleccionadosPrefix(configParent, sourceChildren)
      : null;

    // Sin Ext/Sel: esperar hijas hidratadas si el listado ya las tiene.
    if (!plastyvergPrefix) {
      if (listChildren.length > 0 && childTasks.length === 0 && !hydrationTimedOut) {
        const timer = setTimeout(() => setHydrationTimedOut(true), 2000);
        return () => clearTimeout(timer);
      }
    }

    let cancelled = false;
    const availableKeys = questions.map((question) => question.key);

    void (async () => {
      let configModelo: any = null;
      try {
        configModelo = await resolveParentFieldsConfigModelo(
          configParent,
          sourceChildren,
          resolvedTenantId,
          layoutMapKey,
        );
      } catch (error) {
        console.warn('[useParentFieldsLayout] No se pudo cargar la config de campos del padre', error);
      }
      if (cancelled) return;

      configModeloRef.current = configModelo;

      const storedLayout = filterParentFieldLayoutConfigByKeys(
        resolveStoredParentFieldLayout(configParent, sourceChildren, configModelo, resolvedTenantId, layoutMapKey),
        availableKeys,
      );

      if (storedLayout.fields.length > 0) {
        setLayout(storedLayout);
        readyRef.current = true;
        setReady(true);
        return;
      }

      if (readyRef.current) return;

      const answered = questions.filter((question) => question.answer && question.answer !== '-');
      const defaultQuestions = (answered.length ? answered : questions).slice(0, 8);
      setLayout(normalizeParentFieldLayoutConfig(defaultQuestions.map((question) => question.key)));
      readyRef.current = true;
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    childTasks.length,
    configParent,
    dataReady,
    enabled,
    listChildren.length,
    layoutMapKey,
    parentId,
    qSignature,
    questions,
    resolvedTenantId,
    sourceChildren,
    hydrationTimedOut,
  ]);

  const schedulePersist = useCallback(
    (parentFieldLayout: ParentFieldLayoutConfig) => {
      void (async () => {
        const activeTenantId = tenantIdRef.current;
        // En Plastyverg siempre re-resolver el modelo ID-23/24 (evitar ref stale a widest/hija).
        let configModelo = isPlastyvergTenant(activeTenantId) ? null : configModeloRef.current;
        if (!getRecordId(configModelo)) {
          configModelo = await resolveParentFieldsConfigModelo(
            configParent,
            sourceChildren,
            activeTenantId,
            layoutMapKeyRef.current,
          );
          configModeloRef.current = configModelo;
        }

        const mapKey =
          resolveParentFieldsMapKey(configParent, sourceChildren, activeTenantId, layoutMapKeyRef.current) ||
          plastyvergMapKeyFromModeloTitulo(configModelo?.titulo) ||
          '';

        if (!mapKey) {
          console.warn(
            '[useParentFieldsLayout] No se puede guardar campos: falta key de mapa (ID-23/ID-24 o modelo padre)',
          );
          return;
        }
        if (isPlastyvergTenant(activeTenantId) && !isPlastyvergParentFieldsMapKey(mapKey)) {
          console.warn(
            '[useParentFieldsLayout] Plastyverg solo persiste bajo ID-23/ID-24; key inválida:',
            mapKey,
          );
          return;
        }

        const childModelId = getRecordId(configModelo);
        if (!childModelId) {
          console.warn(
            '[useParentFieldsLayout] No se puede guardar campos: falta modeloTarea de config (Ext/Sel o tabla hijas)',
          );
          return;
        }
        persistTableConfig(configModelo, mapKey, parentFieldLayout);
      })();
    },
    [configParent, persistTableConfig, sourceChildren],
  );

  const applyLayout = useCallback(
    (next: ParentFieldLayoutConfig) => {
      userEditedRef.current = true;
      setUserEdited(true);
      setLayout(next);
      schedulePersist(next);
    },
    [schedulePersist],
  );

  const toggle = useCallback(
    (key: string) => {
      const current = layoutRef.current;
      applyLayout({
        ...current,
        fields: toggleParentFieldInLayout(
          current.fields,
          key,
          Math.min(DEFAULT_PARENT_FIELD_COL_SPAN, current.cellsPerRow),
        ),
      });
    },
    [applyLayout],
  );

  const reorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      const current = layoutRef.current;
      applyLayout({
        ...current,
        fields: reorderParentFieldLayout(current.fields, fromIndex, toIndex),
      });
    },
    [applyLayout],
  );

  const setColSpan = useCallback(
    (key: string, colSpan: number) => {
      const current = layoutRef.current;
      applyLayout({
        ...current,
        fields: setParentFieldColSpanInLayout(current.fields, key, colSpan, current.cellsPerRow),
      });
    },
    [applyLayout],
  );

  const setCellsPerRow = useCallback(
    (cellsPerRow: number) => {
      applyLayout(setParentFieldLayoutCellsPerRow(layoutRef.current, cellsPerRow));
    },
    [applyLayout],
  );

  return {
    questions,
    layout,
    ready,
    saving,
    userEdited,
    applyLayout,
    toggle,
    reorder,
    setColSpan,
    setCellsPerRow,
    configModelo: configModeloRef.current,
  };
}
